// /liff/binding — guest enters an 8-char invite code to join their organisation.
//
// Flow:
//   1. The page boots and waits for the LINE in-app session to be ready.
//   2. The user pastes (or auto-fills via ?code=…) the 8-char invite code.
//   3. POST /v1/auth/binding/redeem { id_token, code, liff_app:"binding" }.
//   4. Success → "已成功加入組織" card → 3-second countdown → close.
//   5. Failure → friendly error message (specific to the failure type).

import React, { useEffect, useState } from "react";
import { LiffProvider, useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import { CheckCircle2, XCircle, KeyRound, Loader2 } from "lucide-react";

const CODE_LEN = 8;
const ALLOWED_CHARS = /[^A-Z0-9]/g;

const ROLE_LABEL: Record<string, string> = {
  tenant_admin:      "管理員",
  manager:           "案場主管",
  officer:           "巡邏員",
  incident_reporter: "通報員",
  auditor:           "稽核員",
  super_admin:       "平台管理員",
};

const FRIENDLY_ERRORS: Record<string, string> = {
  // From /v1/auth/binding/redeem
  code_not_found:           "找不到此邀請碼,請確認是否輸入正確",
  code_already_redeemed:    "此邀請碼已被使用過,請向管理員索取新的",
  code_expired:             "邀請碼已過期,請向管理員索取新的",
  code_revoked:             "此邀請碼已被收回,請向管理員索取新的",
  invalid_code_length:      "邀請碼是 8 個字母或數字",
  code_race_lost:           "綁定發生衝突,請再試一次",
  redeem_upsert_failed:     "綁定資料寫入失敗,請聯絡您的管理員",
  super_admin_already_linked: "此身分已綁定其他 LINE 帳號",
  // From upstream LINE / connection issues
  id_token_invalid:         "您的 LINE 登入已過期,請關閉本頁重新開啟",
  line_verify_unreachable:  "暫時無法連線到 LINE 伺服器,請稍後再試",
  network_error:            "目前無法連線,請檢查網路後再試",
  no_id_token:              "尚未取得 LINE 登入資訊,請關閉本頁重新開啟",
};

interface RedeemResponse {
  ok?: boolean;
  redeemed?: boolean;
  code?: string;
  tenant_id?: string;
  role?: string;
  session?: { role?: string; tenant_id?: string };
  /** When `available: true` the server set `nexus_session` cookies for the
   *  dashboard origin alongside this redeem response. The LIFF page
   *  surfaces a「前往後台」 CTA pointing at /dashboard/ — opening it
   *  inside the same LINE webview lands the operator signed-in,
   *  skipping the second-LINE-OAuth round-trip they'd otherwise hit. */
  dashboard_login?: { available: boolean; expires_at?: number };
  error?: string;
  hint?: string;
  correlation_id?: string;
}

function friendlyError(key: string, hint?: string): string {
  if (FRIENDLY_ERRORS[key]) return FRIENDLY_ERRORS[key];
  if (hint) return hint;
  // Generic fallback (do NOT expose status codes)
  return "目前無法完成綁定,請稍後再試";
}

function BindingForm(): React.ReactElement {
  const { isReady, isAuthed, error: liffError } = useLiff();
  const initial = (() => {
    if (typeof window === "undefined") return "";
    const u = new URL(window.location.href);
    return (u.searchParams.get("code") ?? "")
      .toUpperCase()
      .replace(ALLOWED_CHARS, "")
      .slice(0, CODE_LEN);
  })();
  const [code, setCode] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<RedeemResponse | null>(null);
  const [errKey, setErrKey] = useState<string | null>(null);
  const [errHint, setErrHint] = useState<string | undefined>(undefined);
  const [countdown, setCountdown] = useState(3);

  // Auto-redeem when ?code=… is fully present and the page is ready.
  useEffect(() => {
    if (!isReady) return;
    if (initial.length === CODE_LEN && !done && !busy) {
      void redeem(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  async function redeem(c: string) {
    setBusy(true);
    setErrKey(null);
    setErrHint(undefined);
    try {
      // Read the LINE id_token from the LIFF SDK.
      const liff = (await import("@line/liff")).default;
      const idToken = liff.getIDToken();
      if (!idToken) {
        setErrKey("no_id_token");
        setBusy(false);
        return;
      }
      const r = await apiFetch("/v1/auth/binding/redeem", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id_token: idToken, code: c, liff_app: "binding" }),
      });
      let body: RedeemResponse = {};
      try {
        body = await r.json();
      } catch {
        // Network / non-JSON response — keep going with empty body.
      }
      if (!r.ok || !body.ok) {
        const key = body.error ?? "network_error";
        setErrKey(key);
        setErrHint(body.hint);
        setBusy(false);
        // Console log the raw response so dev tools show specifics.
        console.warn("[binding] redeem failed", { status: r.status, body });
        return;
      }
      setDone(body);
      setBusy(false);
      // 3-second countdown then close the in-app browser so LINE refreshes
      // the user's main menu.
      let n = 3;
      setCountdown(n);
      const timer = setInterval(async () => {
        n -= 1;
        setCountdown(n);
        if (n <= 0) {
          clearInterval(timer);
          try {
            const liff = (await import("@line/liff")).default;
            liff.closeWindow();
          } catch {
            window.close();
          }
        }
      }, 1000);
    } catch (e) {
      console.error("[binding] redeem threw", e);
      setErrKey("network_error");
      setBusy(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || done) return;
    if (code.length !== CODE_LEN) {
      setErrKey("invalid_code_length");
      return;
    }
    void redeem(code);
  }

  // Success card
  if (done) {
    const roleLabel = ROLE_LABEL[done.role ?? ""] ?? "成員";
    const dashboardReady = !!done.dashboard_login?.available;
    return (
      <div className="mx-auto max-w-sm p-6 mt-12 rounded-2xl bg-bg-surface border border-success/40 shadow-xl space-y-3">
        <div className="flex items-center gap-2 text-success">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-semibold">已成功加入組織</span>
        </div>
        <p className="text-sm text-text-primary">
          您的身分:<strong className="ml-1">{roleLabel}</strong>
        </p>
        {dashboardReady ? (
          <>
            <p className="text-xs text-text-secondary leading-relaxed">
              管理者後台已同步登入。點下方按鈕進入後台，或關閉本頁繼續使用 LINE 選單。
            </p>
            <a
              href="/dashboard/"
              className="btn-primary w-full justify-center text-sm"
            >
              前往後台
            </a>
            <p className="text-2xs text-text-muted">
              {countdown > 0
                ? `若不需要進後台,${countdown} 秒後將自動關閉本頁。`
                : "關閉中…"}
            </p>
          </>
        ) : (
          <p className="text-xs text-text-secondary">
            {countdown > 0
              ? `${countdown} 秒後自動關閉本頁,LINE 會立即顯示您專屬的選單`
              : "關閉中…"}
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto max-w-sm p-6 mt-12 rounded-2xl bg-bg-surface border border-border shadow-xl space-y-4">
      <header className="flex items-center gap-2 text-accent">
        <KeyRound className="h-5 w-5" />
        <span className="text-sm font-semibold text-text-primary">完成身分綁定</span>
      </header>
      <p className="text-xs text-text-secondary leading-relaxed">
        請輸入您的主管提供的 8 碼邀請碼。完成後本頁會自動關閉,LINE 會顯示您專屬的功能選單。
      </p>

      {!isReady && (
        <div className="flex items-center gap-2 text-2xs text-text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> 正在連線中…
        </div>
      )}
      {liffError && (
        <div className="text-2xs text-danger">無法連線:{liffError}</div>
      )}

      <label className="block">
        <span className="text-xs text-text-muted">邀請碼</span>
        <input
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={CODE_LEN}
          placeholder="AB23CDFG"
          className="input mt-1 font-mono text-lg tracking-[0.4em] text-center uppercase"
          value={code}
          onChange={(e) => {
            const v = e.target.value.toUpperCase().replace(ALLOWED_CHARS, "").slice(0, CODE_LEN);
            setCode(v);
            setErrKey(null);
            setErrHint(undefined);
          }}
          disabled={busy}
        />
      </label>

      {errKey && (
        <div className="flex items-start gap-2 rounded-md bg-danger/10 border border-danger/30 p-2 text-2xs text-danger">
          <XCircle className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>{friendlyError(errKey, errHint)}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={busy || !isReady || code.length !== CODE_LEN}
        className="btn-primary w-full text-sm"
      >
        {busy ? "綁定中…" : "送出"}
      </button>

      <p className="text-2xs text-text-muted">
        若還沒拿到邀請碼,請聯絡您的主管。每組邀請碼只能使用一次,預設 7 天到期。
      </p>
    </form>
  );
}

export function BindingPage(): React.ReactElement {
  return (
    <LiffProvider page="binding">
      <BindingForm />
    </LiffProvider>
  );
}
